import type React from "react";

import { RequestStatusEnum, RequestTypeEnum } from "@nlc/shared-types";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ChevronLeftIcon,
  CopyIcon,
  EllipsisVerticalIcon,
  FolderIcon,
  ImagePlusIcon,
  LifeBuoyIcon,
  PencilIcon,
  PenLineIcon,
  PlayIcon,
  Sparkles,
  SquarePauseIcon,
  StarIcon,
  Trash2Icon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DialogTrigger, Input, TextField } from "react-aria-components";
import { Controller } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import type { MyFilesSearchSchema } from "@/features/my-files/page-search-schema";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { CheckIcon } from "@/components/icons.tsx";
import { Popover } from "@/components/popover";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard.tsx";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import {
  DesignConfidenceCheckerModal,
  useUploadingPolicy,
} from "@/features/request/design-confidence-checker";
import { SelectCreator } from "@/features/request/edit/components/header/components/SelectCreator";
import { useEditRequestModals } from "@/features/request/edit/contexts/edit-request-modals.context";
import { useCanEditRequestRating } from "@/features/request/edit/hooks/useCanEditRequestRating";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";
import { useToggleCompletion } from "@/features/request/edit/hooks/useToggleCompletion";
import { useTogglePause } from "@/features/request/edit/hooks/useTogglePause";
import {
  invalidateStatusChangeQueries,
  scheduleQueueAutomationRefetch,
} from "@/features/requests/utils/requestInvalidation";
import { WhitelabelBadge } from "@/features/shared";
import { requestKeys } from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import { dateFormatter } from "@/utils/dateFormatter.ts";

export function EditRequestHeader() {
  const { data: request } = useGetRequest();
  const { history } = useRouter();
  const navigate = useNavigate();
  const { canAccess, hasRole } = useRoleAccess();
  const isInternal = hasRole(["super-admin", "team-leader", "designer"]);

  return (
    <div className="flex flex-col gap-lg lg:flex-row lg:items-center lg:px-3xl lg:pt-xl lg:gap-2xl">
      <div className="px-3xl pt-xl lg:px-0 lg:pt-0 flex flex-1 min-w-0 justify-start items-center gap-2xl">
        <Button
          color="gray"
          isIconOnly
          onPress={() =>
            history.canGoBack()
              ? history.go(-1)
              : navigate({ search: { filter: "all" }, to: "/requests" })
          }
          size="lg"
          variant="secondary"
        >
          <ChevronLeftIcon size={20} />
        </Button>

        <div className="inline-flex w-full flex-col justify-start items-start gap-xs">
          <div className="w-full flex items-center gap-xl">
            <Title
              badges={
                <>
                  {request?.deletedAt && (
                    <Badge isRounded size="medium" variant="error">
                      <Trash2Icon size={14} />
                      Deleted
                    </Badge>
                  )}
                  {isInternal && (
                    <WhitelabelBadge
                      role={request?.whitelabelRole}
                      size="medium"
                    />
                  )}
                  {request?.isAiStudio && (
                    <Badge isRounded size="medium" variant="indigo">
                      AI Studio
                    </Badge>
                  )}
                </>
              }
              requestType={request?.requestType}
            />
          </div>
          <FeatureGuard feature="edit-request.created-by.view">
            <div className="hidden sm:flex items-center gap-md whitespace-nowrap">
              <span className="text-md text-text-tertiary">
                Created on {dateFormatter(request?.createdAt)} by
              </span>
              <FeatureGuard
                fallback={<Badge size="small">{request?.creator.name}</Badge>}
                feature="edit-request.created-by.edit"
              >
                <SelectCreator />
              </FeatureGuard>
            </div>
          </FeatureGuard>
        </div>
      </div>

      <div className="px-md md:px-3xl lg:px-0 inline-flex flex-wrap lg:flex-nowrap justify-center items-center gap-1 lg:gap-sm w-full lg:w-fit">
        <Buttons />
        {canAccess("edit-request.kebab-menu") && (
          <KebabMenu requestId={request?.id} />
        )}
      </div>
    </div>
  );
}

function Title({
  badges,
  requestType,
}: {
  badges?: React.ReactNode;
  requestType: null | string | undefined;
}) {
  const [showEditIcon, setShowEditIcon] = useState(false);
  const { debouncedUpdateField } = useFieldUpdate();
  const inputRef = useRef<HTMLInputElement>(null);
  const methods = useEditRequestFormContext();
  const { isLocked } = useTemplateLock();

  const title = methods.watch("name");
  const [isEditingText, setIsEditingText] = useState<boolean>(false);

  useEffect(() => {
    if (isEditingText) {
      inputRef.current?.focus();
    }
  }, [isEditingText]);

  return (
    <Controller
      control={methods.control}
      name="name"
      render={({ field: { name, onBlur, onChange, value } }) => (
        <div
          className="flex grow items-center gap-xl border-none @container"
          onMouseEnter={() => setShowEditIcon(true)}
          onMouseLeave={() => setShowEditIcon(false)}
        >
          <div className="flex flex-wrap items-center content-center gap-1 min-w-0 sm:flex-nowrap sm:h-9 sm:gap-xl">
            <div className="relative min-w-0 sm:flex-1">
              {isLocked ? (
                <span className="line-clamp-1 text-display-xs font-semibold text-text-primary">
                  {value || "Request Name"}
                </span>
              ) : (
                <FeatureGuard
                  fallback={
                    <span className="line-clamp-1 text-display-xs font-semibold text-text-primary">
                      {value || "Request Name"}
                    </span>
                  }
                  feature="edit-request.title.edit"
                >
                  <span
                    className="text-display-xs font-semibold text-text-primary line-clamp-1"
                    onClick={() => setIsEditingText(true)}
                  >
                    {title || " "}
                  </span>
                  <TextField
                    aria-label="Request name"
                    className={twMerge(
                      "absolute inset-0 ",
                      !isEditingText && "invisible pointer-events-none",
                    )}
                    name={name}
                    type="text"
                  >
                    <Input
                      className="size-full p-0 text-ellipsis bg-bg-primary text-display-xs font-semibold text-text-primary outline-none"
                      name={name}
                      onBlur={() => {
                        setIsEditingText(false);

                        return onBlur;
                      }}
                      onChange={(e) => {
                        onChange(e);
                        debouncedUpdateField(name, e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          inputRef.current?.blur();
                        }
                      }}
                      ref={inputRef}
                      value={value || ""}
                    />
                  </TextField>
                </FeatureGuard>
              )}
            </div>

            {requestType && (
              <Badge isRounded size="medium" variant="indigo">
                {parserRequestType(requestType).replace(/^\w/, (c) =>
                  c.toUpperCase(),
                )}
              </Badge>
            )}
            {badges}
          </div>

          {!isLocked && (
            <FeatureGuard feature="edit-request.title.edit">
              <Button
                className="hidden sm:flex size-8 p-none opacity-0 transition-opacity duration-200"
                color="gray"
                isIconOnly
                onPress={() => {
                  setIsEditingText(true);
                  inputRef.current?.focus();
                }}
                style={{ opacity: showEditIcon ? 1 : 0 }}
                variant="secondary"
              >
                <AccessibleIcon aria-label="Edit">
                  <PencilIcon />
                </AccessibleIcon>
              </Button>
            </FeatureGuard>
          )}
        </div>
      )}
    />
  );
}

const parserRequestType = (requestType: string): string => {
  if (!requestType) return "";

  const websiteTypes = [
    RequestTypeEnum.WEBSITE_IMPLEMENTATION,
    RequestTypeEnum.WEBSITE_DESIGN,
  ];

  return websiteTypes.includes(requestType as RequestTypeEnum)
    ? "website"
    : requestType;
};

function Buttons() {
  const { data: request } = useGetRequest();
  const { openCompletionFeedback, openTemplateInfo, openTogglePauseConfirm } =
    useEditRequestModals();
  const { watch } = useEditRequestFormContext();
  const { isPending: isPendingPause } = useTogglePause();
  const { isPending: isPendingCompletion, reopenRequest } =
    useToggleCompletion();
  const parsedRequestType = parserRequestType(request?.requestType ?? "");
  const { isTemplate, requestType } = useIsTemplate();
  const { isLocked } = useTemplateLock();
  const navigate = useNavigate();

  const isCompleted = watch("statusId") === RequestStatusEnum.Completed;

  const viewFilesSearch: MyFilesSearchSchema = {
    brandId: request?.brandId ?? "unknown",
    projectId: request?.projectId,
    requestId: request?.id,
    requestType: parsedRequestType
      ? (parsedRequestType as MyFilesSearchSchema["requestType"])
      : undefined,
  };

  const uploadSearch: MyFilesSearchSchema = {
    ...viewFilesSearch,
    openUpload: true,
  };

  const { isConfidenceChecker } = useUploadingPolicy(request?.id);

  const [isCheckerOpen, setIsCheckerOpen] = useState(false);

  const handleViewFiles = () => {
    if (isTemplate) {
      navigate({
        search: {
          requestType:
            requestType === RequestTypeEnum.VIDEO ? "video" : "graphic",
        },
        to: "/my-files/template",
      });
    } else {
      navigate({ search: viewFilesSearch, to: "/my-files" });
    }
  };

  return (
    <div className="flex h-fit flex-wrap gap-1.5">
      <Button color="gray" onPress={handleViewFiles} variant="secondary">
        <AccessibleIcon>
          <FolderIcon />
        </AccessibleIcon>
        View files
      </Button>

      <FeatureGuard feature="edit-request.upload-file">
        {isConfidenceChecker ? (
          <Button
            color="color"
            onPress={() => setIsCheckerOpen(true)}
            variant="secondary"
          >
            <AccessibleIcon>
              <ImagePlusIcon />
            </AccessibleIcon>
            Upload designs
          </Button>
        ) : (
          <Link
            data-cy="upload-designs"
            search={uploadSearch}
            target="_blank"
            to="/my-files"
          >
            <Button color="color" variant="secondary">
              <AccessibleIcon>
                <ImagePlusIcon />
              </AccessibleIcon>
              Upload designs
            </Button>
          </Link>
        )}
      </FeatureGuard>

      {isConfidenceChecker && request !== undefined && (
        <DesignConfidenceCheckerModal
          isOpen={isCheckerOpen}
          onOpenChange={setIsCheckerOpen}
          requestId={request.id}
        />
      )}

      <FeatureGuard feature="edit-request.pause">
        {!isCompleted && (
          <Button
            color="gray"
            isDisabled={isPendingPause || isTemplate || isLocked}
            onPress={isTemplate ? openTemplateInfo : openTogglePauseConfirm}
            size="sm"
            variant="secondary"
          >
            <AccessibleIcon>
              {RequestStatusEnum.Paused === watch("statusId") ? (
                <PlayIcon />
              ) : (
                <SquarePauseIcon />
              )}
            </AccessibleIcon>
            {isPendingPause
              ? "Processing..."
              : RequestStatusEnum.Paused === watch("statusId")
                ? "Resume"
                : "Pause"}
          </Button>
        )}
      </FeatureGuard>

      <FeatureGuard feature="edit-request.complete">
        {isCompleted ? (
          <Button
            color="color"
            isDisabled={isPendingCompletion || isLocked}
            onPress={isTemplate ? openTemplateInfo : reopenRequest}
            variant="secondary"
          >
            <AccessibleIcon>
              <PlayIcon />
            </AccessibleIcon>
            {isPendingCompletion ? "Reopening..." : "Reopen"}
          </Button>
        ) : (
          <Button
            color="color"
            isDisabled={isLocked}
            onPress={isTemplate ? openTemplateInfo : openCompletionFeedback}
            variant="secondary"
          >
            <AccessibleIcon>
              <CheckIcon />
            </AccessibleIcon>
            Complete
          </Button>
        )}
      </FeatureGuard>
    </div>
  );
}

function KebabMenu({ requestId }: { requestId: number | undefined }) {
  const {
    openChangeAccount,
    openChangeType,
    openDeleteModal,
    openDuplicateRequest,
    openEditRating,
    openHavingProblems,
    openUnlock,
  } = useEditRequestModals();
  const { isTemplate } = useIsTemplate();
  const { isLocked, isRealTemplate } = useTemplateLock();
  const { updateField } = useFieldUpdate();
  const queryClient = useQueryClient();
  const { watch } = useEditRequestFormContext();
  const { hasRole } = useRoleAccess();
  const navigate = useNavigate();

  const isSupervisory = hasRole(["super-admin", "team-leader"]);
  const isArchived = watch("statusId") === RequestStatusEnum.Archived;
  const canEditRating = useCanEditRequestRating(requestId, isArchived);

  const archiveRequest = async (close: () => void) => {
    await updateField("statusId", RequestStatusEnum.Archived);
    await updateField("dueDate", null);
    if (requestId) {
      await queryClient.invalidateQueries({
        queryKey: requestKeys.requestById(requestId),
      });
      invalidateStatusChangeQueries(queryClient);
    }
    close();
  };

  const unarchiveRequest = async (close: () => void) => {
    await updateField("statusId", RequestStatusEnum.Queued);
    if (requestId) {
      await queryClient.invalidateQueries({
        queryKey: requestKeys.requestById(requestId),
      });
      invalidateStatusChangeQueries(queryClient);
      scheduleQueueAutomationRefetch(queryClient, [
        requestKeys.requestById(requestId),
      ]);
    }
    close();
  };

  return (
    <DialogTrigger>
      <Button variant="unstyled">
        <AccessibleIcon className="text-fg-quaternary">
          <EllipsisVerticalIcon />
        </AccessibleIcon>
      </Button>
      <Popover className="flex flex-col" placement="bottom end">
        <Dialog>
          {({ close }) => (
            <>
              {!isRealTemplate && (
                <FeatureGuard feature="edit-request.kebab-menu.delete">
                  <KebabMenuItem
                    icon={<Trash2Icon />}
                    label="Delete Request"
                    onPress={() => {
                      openDeleteModal();
                      close();
                    }}
                  />
                </FeatureGuard>
              )}

              <KebabMenuItem
                icon={<CopyIcon />}
                label="Duplicate request"
                onPress={() => {
                  openDuplicateRequest();
                  close();
                }}
              />

              <FeatureGuard feature="edit-request.kebab-menu.change-account">
                <KebabMenuItem
                  icon={<FolderIcon />}
                  isDisabled={isLocked}
                  label="Change account"
                  onPress={() => {
                    if (isTemplate) openUnlock();
                    else openChangeAccount();
                    close();
                  }}
                />
              </FeatureGuard>

              <KebabMenuItem
                icon={<PenLineIcon />}
                isDisabled={isLocked}
                label="Change request type"
                onPress={() => {
                  openChangeType();
                  close();
                }}
              />

              {canEditRating && (
                <KebabMenuItem
                  icon={<StarIcon />}
                  label="Edit rating"
                  onPress={() => {
                    openEditRating();
                    close();
                  }}
                />
              )}

              {isSupervisory && requestId !== undefined && (
                <KebabMenuItem
                  icon={<Sparkles />}
                  label="Confidence summary"
                  onPress={() => {
                    navigate({
                      to: `/requests/${requestId}/confidence-checker-summary`,
                    });
                    close();
                  }}
                />
              )}

              <FeatureGuard feature="edit-request.kebab-menu.archive">
                {isArchived ? (
                  <KebabMenuItem
                    icon={<ArchiveRestoreIcon />}
                    isDisabled={isLocked}
                    label="Unarchive request"
                    onPress={() => unarchiveRequest(close)}
                  />
                ) : (
                  <KebabMenuItem
                    icon={<ArchiveIcon />}
                    isDisabled={isLocked}
                    label="Archive request"
                    onPress={() => archiveRequest(close)}
                  />
                )}
              </FeatureGuard>

              <FeatureGuard feature="edit-request.kebab-menu.having-problem">
                <div className="border-t border-border-secondary">
                  <KebabMenuItem
                    className="border border-border-secondary"
                    icon={<LifeBuoyIcon />}
                    isDisabled={isLocked}
                    label="Having problems?"
                    onPress={() => {
                      openHavingProblems();
                      close();
                    }}
                  />
                </div>
              </FeatureGuard>
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
interface KebabMenuItemProps {
  className?: string;
  icon: React.ReactNode;
  isDisabled?: boolean;
  label: string;
  onPress?: () => void;
}

function KebabMenuItem({
  className,
  icon,
  isDisabled,
  label,
  onPress,
}: KebabMenuItemProps) {
  return (
    <Button
      className={twMerge(
        "flex justify-start border-none px-4 py-2.5 text-text-secondary ",
        className,
      )}
      color="gray"
      isDisabled={isDisabled}
      onPress={onPress}
      variant="secondary"
    >
      <AccessibleIcon className="text-fg-quaternary">{icon}</AccessibleIcon>
      {label}
    </Button>
  );
}
